import type { TotalNumberOfContractsListParams, TotalNumberOfContractsSortKey } from '../types/pages'
/**
 * Total Number Of Contracts — React parity with Angular `ReportsTotalNumberContractComponent`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronsUpDown, DollarSign, Download, Eye, Loader2, Receipt, Wallet } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { getBrowserApiBaseUrl } from '@/config/env'
import { ROUTE_BUILDERS } from '@/config/routes'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import { showApiError } from '@/lib/utils/notifications'
import { formatAmount } from '@/lib/utils/formatters'
import { cn } from '@lib/utils/helpers'
import { useUserRole } from '@/hooks/useUserRole'
import { ReportOverviewMetrics } from '../components/ReportOverviewMetrics'
import { ReportTableScroll } from '../components/ReportTableScroll'
import reportStyles from '../styles/reportsTable.module.css'
import { formatReportDate } from '../utils/reportDateDisplay'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import {
  TotalContractsFilters,
  type TotalContractsFilterState,
} from '../components/TotalContractsFilters'
import { countActiveTotalContractsFilters } from '../utils/totalContractsFilterCount'
import { getGatewayStatuses, getTotalContractList } from '../services/reportsService'
import type { TotalContractListResponse, TotalContractRow } from '../types/api'
import { ymdToDmy } from '../utils/reportDateParams'
import {
  buildPaymentLogsSessionFromParams,
  clearPaymentLogsSession,
  readPaymentLogsSession,
  sessionFilterToTotalContractsPatch,
  writePaymentLogsSession,
} from '../utils/paymentLogsSessionFilter'
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import {
  DEFAULT_PARAMS,
  DEFAULT_TYPES,
  SORT_COLUMNS,
} from '../constants/pages/totalNumberOfContractsPage'
import { REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'

function initialParams(): TotalNumberOfContractsListParams {
  const session = readPaymentLogsSession()
  if (session) {
    const { patch } = sessionFilterToTotalContractsPatch(session)
    return {
      ...DEFAULT_PARAMS,
      ...patch,
      type: patch.type?.length ? patch.type : [...DEFAULT_TYPES],
    }
  }
  return { ...DEFAULT_PARAMS }
}

function buildTotalContractQueryParams(p: TotalNumberOfContractsListParams): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.q.trim()) qs.set('q', p.q.trim())
  if (p.company_id) qs.set('company_id', p.company_id)
  if (p.property_id) qs.set('property_id', p.property_id)
  const fromApi = ymdToDmy(p.fromDt)
  const toApi = ymdToDmy(p.toDt)
  if (fromApi) qs.set('fromDt', fromApi)
  if (toApi) qs.set('toDt', toApi)
  const csFrom = ymdToDmy(p.contract_start_fromDt)
  const csTo = ymdToDmy(p.contract_start_toDt)
  if (csFrom) qs.set('contract_start_fromDt', csFrom)
  if (csTo) qs.set('contract_start_toDt', csTo)
  for (const t of p.type) {
    if (t) qs.append('type[]', t)
  }
  if (p.source.trim()) qs.set('source', p.source.trim())
  for (const s of p.status) {
    if (s) qs.append('status[]', s)
  }
  for (const g of p.gateway_status) {
    if (g) qs.append('gateway_status[]', g)
  }
  if (p.special_note.trim()) qs.set('special_note', p.special_note.trim())
  return qs
}

function buildTotalContractExportHref(p: TotalNumberOfContractsListParams): string {
  const token = getAuthTokenFromStorage()
  if (!token) return ''
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.q.trim()) qs.set('q', p.q.trim())
  if (p.company_id) qs.set('company_id', p.company_id)
  if (p.property_id) qs.set('property_id', p.property_id)
  const fromApi = ymdToDmy(p.fromDt)
  const toApi = ymdToDmy(p.toDt)
  if (fromApi) qs.set('fromDt', fromApi)
  if (toApi) qs.set('toDt', toApi)
  const csFrom = ymdToDmy(p.contract_start_fromDt)
  const csTo = ymdToDmy(p.contract_start_toDt)
  if (csFrom) qs.set('contract_start_fromDt', csFrom)
  if (csTo) qs.set('contract_start_toDt', csTo)
  p.status.forEach((s, i) => {
    if (s) qs.set(`status[${i}]`, s)
  })
  p.gateway_status.forEach((g, i) => {
    if (g) qs.set(`gateway_status[${i}]`, g)
  })
  p.type.forEach((t, i) => {
    if (t) qs.set(`type[${i}]`, t)
  })
  if (p.source.trim()) qs.set('source', p.source.trim())
  if (p.special_note.trim()) qs.set('special_note', p.special_note.trim())
  qs.set('access_token', token)
  qs.set('view', 'xlsx')
  return `${getBrowserApiBaseUrl()}/api/export/total_contract?${qs.toString()}`
}

function formatRowAmount(row: TotalContractRow): string {
  const amount = Number(row.payment_amount ?? 0)
  const surcharge = Number(row.payment_log_surcharge_fee ?? 0)
  const refund = Number(row.payment_log_refund_fee ?? 0)
  if (surcharge && surcharge < amount) {
    return formatAmount(amount - surcharge)
  }
  return formatAmount(amount + (refund ? refund * -1 : 0))
}

function formatDisplayDate(value: string | undefined): string {
  return formatReportDate(value)
}

function padApplicantId(id: number | undefined): string {
  if (id == null) return ''
  return String(id).padStart(8, '0')
}

function padApplicationId(id: number | undefined): string {
  if (id == null) return ''
  return String(id).padStart(6, '0')
}

export function TotalNumberOfContractsPage() {
  const { hasWriteAccess, companyId, propertyId } = useUserRole()
  const canOpenApplication = hasWriteAccess('application')

  const [params, setParams] = useState<TotalNumberOfContractsListParams>(initialParams)
  const [searchDraft, setSearchDraft] = useState(() => params.q)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [rows, setRows] = useState<TotalContractRow[]>([])
  const [totals, setTotals] =
    useState<TotalContractListResponse['total_count']>()
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [gatewayStatusOptions, setGatewayStatusOptions] = useState<
    SearchableSelectOption[]
  >([])
  const [exportWarnOpen, setExportWarnOpen] = useState(false)

  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  useEffect(() => {
    setSearchDraft(params.q)
  }, [params.q])

  useEffect(() => {
    void getGatewayStatuses()
      .then((meta) => {
        const opts: SearchableSelectOption[] = []
        Object.keys(meta).forEach((source) => {
          const bucket = meta[source]
          if (bucket && typeof bucket === 'object') {
            Object.keys(bucket).forEach((code) => {
              opts.push({ value: code, label: code })
            })
          }
        })
        setGatewayStatusOptions(opts)
      })
      .catch(() => {
        /* optional metadata */
      })
  }, [])

  const load = useCallback(async (next: TotalNumberOfContractsListParams) => {
    setLoading(true)
    try {
      const res = await getTotalContractList(
        buildTotalContractQueryParams(next),
      )
      setRows(res.data ?? [])
      setTotals(res.total_count)
      setPageOptions({
        current: res.current ?? 1,
        last: Math.max(1, res.last ?? 1),
        total: res.total ?? 0,
      })
      writePaymentLogsSession(buildPaymentLogsSessionFromParams(next))
    } catch (err) {
      showApiError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(params)
  }, [load, params])

  const drawerFilters: TotalContractsFilterState = useMemo(
    () => ({
      company_id: params.company_id,
      property_id: params.property_id,
      fromDt: params.fromDt,
      toDt: params.toDt,
      contract_start_fromDt: params.contract_start_fromDt,
      contract_start_toDt: params.contract_start_toDt,
      source: params.source,
      status: params.status,
      gateway_status: params.gateway_status,
      special_note: params.special_note,
    }),
    [params],
  )

  const activeFilterCount = useMemo(
    () =>
      countActiveTotalContractsFilters(
        { ...drawerFilters, q: params.q },
        lockedCompanyId,
        lockedPropertyId,
      ),
    [drawerFilters, params.q, lockedCompanyId, lockedPropertyId],
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextQ = searchDraft.trim()
      if (nextQ !== params.q.trim()) {
        setParams((prev) => ({ ...prev, q: nextQ, page: 1 }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, params.q])

  const applyDrawerFilters = useCallback((next: TotalContractsFilterState) => {
    setParams((prev) => ({
      ...prev,
      ...next,
      page: 1,
    }))
  }, [])

  const resetFilters = () => {
    setSearchDraft('')
    clearPaymentLogsSession()
    setParams({ ...DEFAULT_PARAMS })
  }

  const sort = (orderBy: TotalNumberOfContractsSortKey) => {
    setParams((prev) => ({
      ...prev,
      sortBy:
        prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
      orderBy,
      page: 1,
    }))
  }

  const exportHref = useMemo(
    () => buildTotalContractExportHref(params),
    [params],
  )

  const handleExportClick = () => {
    if (pageOptions.total > 10000) {
      setExportWarnOpen(true)
    }
  }

  const showTotalsStrip = Boolean(totals && Object.keys(totals).length > 0)

  return (
    <div data-testid="total-number-of-contracts-page">
      <PageShell
        title="Total Number Of Contracts"
        pageHeaderTestIdRoot="total-contracts-page-header"
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchDraft}
          searchPlaceholder="Search.."
          onSearchChange={setSearchDraft}
          onOpenFilters={() => setIsFilterOpen(true)}
          activeFilterCount={activeFilterCount}
          testIdPrefix="total-contracts"
          actions={
            exportHref ? (
              <a
                href={exportHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleExportClick}
                className={REPORT_EXPORT_BUTTON_CLASS}
              >
                <Download className="h-[18px] w-[18px]" aria-hidden />
                Export
              </a>
            ) : undefined
          }
        />

        <TotalContractsFilters
          filters={drawerFilters}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={applyDrawerFilters}
          onReset={resetFilters}
          gatewayStatusOptions={gatewayStatusOptions}
        />

        {showTotalsStrip ? (
          <ReportOverviewMetrics
            title="Contracts overview"
            subtitle="Current report totals"
            testIdPrefix="total-contracts"
            loading={loading}
            items={[
              {
                id: 'base-amount',
                label: 'Base Amount',
                value:
                  totals?.base_amount != null && Number(totals.base_amount)
                    ? formatAmount(totals.base_amount)
                    : '$0.00',
                icon: <DollarSign className="h-6 w-6" aria-hidden />,
                tone: 'blue',
              },
              {
                id: 'service-fee',
                label: 'Service Fee',
                value:
                  totals?.service_fee === 0 || totals?.service_fee === '0'
                    ? '$0.00'
                    : formatAmount(totals?.service_fee ?? 0),
                icon: <Receipt className="h-6 w-6" aria-hidden />,
                tone: 'violet',
              },
              {
                id: 'total-amount',
                label: 'Total Amount',
                value:
                  totals?.payment_amount != null && Number(totals.payment_amount)
                    ? formatAmount(totals.payment_amount)
                    : '$0.00',
                icon: <Wallet className="h-6 w-6" aria-hidden />,
                tone: 'sky',
              },
            ]}
          />
        ) : null}

        <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="total-contracts"
            summaryLayout="angular"
          />

          {loading ? (
            <div className="flex min-h-[12rem] items-center justify-center py-16">
              <Loader2
                className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
                aria-hidden
              />
            </div>
          ) : (
            <ReportTableScroll
              enabled={rows.length > 0}
              testIdPrefix="total-contracts"
            >
              <table
                className={cn(
                  reportStyles.listingTable,
                  'border-collapse text-left text-slate-800',
                  '[&_thead_th]:px-2 [&_tbody_td]:px-2 sm:[&_thead_th]:px-2.5 sm:[&_tbody_td]:px-2.5',
                )}
              >
                <thead>
                  <tr>
                    {SORT_COLUMNS.map((col) => {
                      const active = params.orderBy === col.key
                      return (
                        <th key={col.key} className="text-left">
                          <button
                            type="button"
                            onClick={() => sort(col.key)}
                            className={cn(
                              'inline-flex items-center gap-1.5 text-left font-semibold text-slate-600 hover:text-slate-900',
                              active && 'text-[rgb(21_151_255)]',
                            )}
                          >
                            <span>{col.label}</span>
                            <span className={cn('inline-flex items-center ml-1 text-[11px]', active ? 'text-[#1597ff]' : 'text-slate-400')}>
                              {active ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3 shrink-0" aria-hidden />}
                            </span>
                          </button>
                        </th>
                      )
                    })}
                    <th className="text-right font-semibold text-slate-600">
                      View
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={SORT_COLUMNS.length + 1}
                        className="py-12 text-center text-slate-500"
                      >
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr
                        key={row.payment_log_id ?? `row-${index}`}
                        className="border-b border-slate-100"
                      >
                        <td>{row.company_name ?? '—'}</td>
                        <td>{row.property_name ?? '—'}</td>
                        <td>
                          {row.applicant_id != null ? (
                            canOpenApplication ? (
                              <Link
                                to={ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(
                                  row.applicant_id,
                                )}
                                className="text-[#007BFF] hover:underline"
                              >
                                {padApplicantId(row.applicant_id)}
                              </Link>
                            ) : (
                              padApplicantId(row.applicant_id)
                            )
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{padApplicationId(row.application_id)}</td>
                        <td>
                          {[row.first_name, row.last_name]
                            .filter(Boolean)
                            .join(' ') || '—'}
                        </td>
                        <td>{formatRowAmount(row)}</td>
                        <td>{formatDisplayDate(row.purchase_date)}</td>
                        <td>{formatDisplayDate(row.lease_start_date)}</td>
                        <td className="text-right">
                          {row.payment_log_id != null ? (
                            <Link
                              to={`/reports/total-number-of-contract/${row.payment_log_id}`}
                              className="inline-flex rounded-lg p-2 text-slate-600 hover:bg-slate-100"
                              title="View Log"
                            >
                              <Eye className="h-4 w-4" aria-hidden />
                            </Link>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ReportTableScroll>
          )}

          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="total-contracts"
            summaryLayout="angular"
          />
        </div>
      </PageShell>

      <ConfirmDialog
        isOpen={exportWarnOpen}
        title="Export Warning"
        message="The results contain more than 10,000 records. If you export, some Filters or Pivot Table features may not work correctly. Consider narrowing filters for fewer than 10,000 rows."
        primaryLabel="Ok"
        onPrimary={() => setExportWarnOpen(false)}
        secondaryLabel=""
        onSecondary={() => setExportWarnOpen(false)}
        onDismiss={() => setExportWarnOpen(false)}
        sectionedChrome
        messageAlign="center"
        messageWeight="normal"
        presentation="saveChanges"
      />
    </div>
  )
}
